<template>
    <div class="row">
        <div class="col-12 mx-auto">
            <div class="vstack gap-4">
                <div class="d-flex flex-column flex-md-row gap-3 justify-content-between">
                    <div class="d-flex gap-3">
                        <div class="input-group input-group-sm input-group-inline">
                            <button class="btn btn-sm btn-neutral text-primary" @click="previousWeek" :disabled="loading">
                                <i class="bi bi-arrow-left"></i>
                            </button>
                            <span class="input-group-text">
                                <i class="bi bi-calendar"></i>
                            </span>
                            <flat-pickr :config="flatpickr" v-model="today" :disabled="loading" />
                            <button class="btn btn-sm btn-neutral text-primary" @click="nextWeek" :disabled="loading">
                                <i class="bi bi-arrow-right"></i>
                            </button>
                        </div>
                        <button class="btn btn-sm btn-primary" @click="openModal('create_shift_modal')" v-can="'shifts.store'">
                            <i class="bi bi-plus"></i>
                        </button>
                    </div>
                    <div class="d-flex gap-3">
                        <button class="btn btn-sm btn-neutral" @click="downloadSchedule" v-can="'reports.export'" :title="$trans.get('__JSON__.Export to Excel')" tooltip :disabled="exporting || loading">
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="exporting"></span>
                            <i class="bi bi-filetype-xls" v-else></i>
                        </button>
                        <div class="input-group input-group-sm" v-for="filter in filters" v-show="filter.options.length > 1">
                            <span class="input-group-text">
                                <i :class="filter.icon"></i>
                            </span>
                            <vue-multiselect
                                :options="filter.options"
                                :multiple="filter.type === 'multi-select'"
                                :close-on-select="filter.type !== 'multi-select'"
                                :preserve-search="true"
                                :disabled="loading"
                                :placeholder="$trans.get('__JSON__.Make your selection')"
                                :selectLabel="$trans.get('__JSON__.Hit enter to select')"
                                :selectGroupLabel="$trans.get('__JSON__.Hit enter to select the group')"
                                :selectedLabel="$trans.get('__JSON__.Selected')"
                                :deselectLabel="$trans.get('__JSON__.Hit enter to deselect')"
                                :deselectGroupLabel="$trans.get('__JSON__.Hit enter to deselect the group')"
                                label="label"
                                track-by="key"
                                v-model="filterValues[filter.key]"
                                class="form-select form-select-sm"
                            />
                        </div>
                    </div>
                </div>

                <div class="bg-card rounded border-0">
                    <div class="card-body p-0" style="height: 75vh; overflow-y: auto">
                        <div class="alert alert-danger" role="alert" v-if="hasError">
                            <div class="d-flex align-items-center">
                                <div class="w-8 text-lg">
                                    <i class="bi bi-exclamation-circle"></i>
                                </div>
                                <span class="font-bold">{{ error.statusText }}</span>
                            </div>
                            <div class="ps-8">
                                <p class="text-sm">
                                    {{ error.message }}
                                </p>
                                <ul class="m-0 mt-1 ps-3">
                                </ul>
                                <div class="mt-2 mx-n3">
                                    <a href="#" class="alert-link text-sm font-semibold mx-3" @click="fetch">
                                        {{ $trans.get('__JSON__.Try again') }}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div class="position-relative" v-else>
                            <table class="table table-striped table-nowrap table-shifts">
                                <thead class="sticky-top top-0">
                                <tr class="bg-white">
                                    <th class="col-1" scope="col" v-for="date in dates" :key="date" :class="{ 'today': isToday(date) }">
                                        <div class="text-center">
                                            <h6 class="d-block">{{ formatDate(date, DATE_DAY_OF_WEEK) }}</h6>
                                            <span class="d-block text-muted">{{ formatDate(date, DATE_YMD) }}</span>
                                        </div>
                                    </th>
                                </tr>
                                </thead>
                                <tbody v-if="loading">
                                <tr>
                                    <th class="text-center text-muted" colspan="7">
                                        <div class="text-center">
                                            <div class="spinner-border spinner-border-sm" role="status">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                </tbody>
                                <tbody v-for="(department, index) in data" :key="index" v-else-if="data.length">
                                    <tr class="sticky-top safe-top bg-gray-50">
                                        <th class="table-header" colspan="7" scope="row">
                                            {{ department.details.name }}
                                        </th>
                                    </tr>

                                    <tr class="align-top">
                                        <td class="col-1" v-for="date in dates" :key="date" v-if="Object.keys(department.shifts).length > 0">
                                            <div class="shift-card rounded p-0 m-0 mb-5" v-for="shift in department.shifts[date]" :key="shift.id" @click="openShift(shift.id)" :class="cardBgColor(shift)">
                                                <div class="card-header m-0 p-1" v-if="! isEnrolled(shift)">
                                                    <span class="badge rounded-pill d-inline-flex align-items-center ms-auto float-end" :class="badgeBgColor(shift)">
                                                        {{ slotsLeft(shift) }}/{{ slotsTotal(shift) }}
                                                    </span>
                                                </div>
                                                <div class="card-body m-0 p-2" :class="textColor(shift)">
                                                    <div class="d-flex justify-content-between">
                                                        <div>
                                                            <h6 :class="textColor(shift)" v-if="isEnrolled(shift)">{{ currentUserName }}</h6>
                                                            <h6 :class="textColor(shift)" v-else>{{ shift.details.job }}</h6>
                                                            <div class="progress-text font-bold d-block">{{ formatDate(shift.details.start, TIME_HHMM) }} - {{ formatDate(shift.details.end, TIME_HHMM) }}</div>
                                                            <small :class="textColor(shift, true)">{{ formatDate(shift.details.start, DATE_SHORT) }}</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="text-center" colspan="7" v-else>
                                            <span class="text-muted font-italic">
                                                {{ $trans.get('__JSON__.No plannend shifts') }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody v-else>
                                <tr>
                                    <th class="text-center" colspan="7">
                                        <span class="text-muted font-italic">
                                            {{ $trans.get('__JSON__.No plannend shifts') }}
                                        </span>
                                    </th>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <AddNewShift :today="today" v-can="'shifts.store'" />
    <ShiftDetailsModal :shiftId="selected.shiftId" :user="user" v-if="selected.shiftId" />
</template>

<style lang="scss" scoped>
.safe-top {
    top: 67px;
}
</style>

<script>
import { DateTime } from 'luxon'
import ShiftDetailsModal from './ShiftDetailsModalComponent.vue'
import AddNewShift from './AddNewShiftComponent.vue'
import FlatPickr from 'vue-flatpickr-component'
import { Dutch } from 'flatpickr/dist/l10n/nl.js'
import 'flatpickr/dist/flatpickr.css'
import DateHelpers from '../mixins/DateHelpers'
import ModalHelper from '../mixins/ModalHelper'
import ScheduleHelpers from './../mixins/ScheduleHelpers'
import RecurringShiftConfirmation from '../shared/RecurringShiftConfirmationModal.vue'

export default {
    name: 'ShiftsSchedule',
    props: {
        request: {
            type: String,
            required: false,
            default: {}
        },
    },
    mixins: [ScheduleHelpers, DateHelpers, ModalHelper],
    components: {RecurringShiftConfirmation, FlatPickr, ShiftDetailsModal, AddNewShift},
    computed: {
        hasError() {
            return Object.keys(this.error).length > 0
        },
        departments() {
            return this.data.map(it => it.details.id)
        },
    },
    data() {
        return {
            loading: false,
            fetching: false,
            isListening: false,
            exporting: false,
            error: {},

            today: DateTime.now().toISODate(),
            filters: [],
            filterValues: {},
            dates: [],
            data: [],
            locations: [],
            user: null,

            flatpickr: {
                locale: document.documentElement.lang.slice(0, 2),
                wrap: true,
                weekNumbers: true,
                altInput: true,
                altFormat: `\\Week\\ W - Y`,
                altInputClass: 'form-select form-select-sm',
                dateFormat: 'Y-m-d',
                plugins: [require('flatpickr/dist/plugins/weekSelect/weekSelect')({})],
            },

            selected: {
                shiftId: null,
            },
        }
    },
    watch: {
        today(newValue, oldValue) {
            if (DateTime.fromISO(newValue).toISOWeekDate().slice(0, 8) === DateTime.fromISO(oldValue).toISOWeekDate().slice(0, 8)) return
            this.fetch()
        },
        departments() {
            if (this.departments.length > 0) this.listenForEvents()
        },
        selected: {
            handler() {
                if (this.selected.shiftId) setTimeout(() => {
                    let modal = document.getElementById('shift_details_modal')
                    modal.addEventListener('hide.bs.modal', () => this.selected.shiftId = null)
                }, 200)
            },
            deep: true
        },
        filterValues: {
            handler() {
                this.fetch()
            },
            deep: true,
        },
    },
    methods: {
        fetch(init = true) {
            if (this.fetching) return // abort other requests if already fetching

            if (init) {
                this.loading = true
                this.dates = []
            }

            this.fetching = true
            this.error = {}

            axios.post('/api/shifts', {
                today: this.today,
                filters: this.filterValues,
            })
                .then(response => {
                    const data = response.data

                    if (! init) {
                        this.data = data.data
                        return
                    }

                    this.filters = data.filters
                    this.today = data.today
                    this.dates = data.dates
                    this.data = data.data
                    this.locations = data.locations
                    this.user = data.user
                })
                .catch(error => this.catchError(error))
                .finally(() => {
                    if (init) this.loading = false
                    this.fetching = false
                })
        },
        listenForEvents() {
            if (! process.env.MIX_SOCKETS_ENABLED) return

            if (this.isListening) return

            for (let current of this.departments) {
                window.Echo.private(`shifts.${current}`)
                    .listen('.ShiftCreated', event => this.handleBroadcastEvent(event, 'created'))
                    .listen('.ShiftUpdated', event => this.handleBroadcastEvent(event, 'updated'))
                    .listen('.RefreshAll', event => this.handleBroadcastEvent(event, 'refresh'))
            }

            this.isListening = true
        },
        handleBroadcastEvent(event, type) {
            this.fetch(false)
        },
        openShift(shiftId) {
            this.selected.shiftId = shiftId
            setTimeout(async () => await this.openModal('shift_details_modal'), 100)
        },
        previousWeek() {
            this.today = DateTime.fromISO(this.today).minus({ weeks: 1 }).toISODate()
        },
        nextWeek() {
            this.today = DateTime.fromISO(this.today).plus({ weeks: 1 }).toISODate()
        },
        textColor(shift, muted = false) {
            let color = 'dark'
            const bgColor = this.cardBgColor(shift)

            if (['bg-primary', 'bg-success'].includes(bgColor)) color = 'white'

            if (muted) return `text-${color} opacity-50`
            else return `text-${color}`
        },
        cardBgColor(shift) {
            const status = shift.details.status

            if (shift.details.slots.total === 0) return 'bg-light'

            if (this.isEmployee) {
                if (this.isEnrolled(shift) && status === 'is_running') return 'bg-light-success'
                else if (status === 'past-enrolled') return 'bg-light'
                else if (status === 'enrolled') return 'bg-success'
                else if (status === 'past') return 'bg-light'
                else if (status === 'open') return 'bg-primary'
                else if (status === 'full') {
                    if (this.isEnrolled(shift)) return 'bg-success'
                    else return 'bg-light'
                }
                return 'bg-light'
            }

            if (status === 'past') return 'bg-light'
            else if (status === 'open') return 'bg-primary'
            else if (status === 'full') return 'bg-success'
            return 'bg-light'
        },
        badgeBgColor(shift) {
            let color = 'light'

            const status = shift.details.status
            const fullyEnrolled = shift.details.slots.total > 0 && shift.details.slots.taken === shift.details.slots.total
            const cardBgColor = this.cardBgColor(shift)

            if (this.isEmployee) {
                if (status === 'past-enrolled') color = 'success'
                else if (status === 'open') color = 'primary'
                else if (cardBgColor === 'bg-light') return `bg-soft-${color} text-dark`
                else if (status === 'already-enrolled') color = 'light'
            } else {
                if (status === 'past' && fullyEnrolled) color = 'success'
                else if (status === 'past' && ! fullyEnrolled && shift.details.slots.total === 0) return `bg-soft-${color} text-dark`
                else if (status === 'past' && ! fullyEnrolled) color = 'primary'
                else if (status === 'open') color = 'primary'
                else if (fullyEnrolled) color = 'success'
                else if (cardBgColor === 'bg-light') return `bg-soft-${color} text-dark`
            }

            return `bg-soft-${color} text-${color}`
        },
        reset() {
            this.filters = []
            this.filterValues = {}
            this.dates = []
            this.data = []
            this.locations = []
            this.user = null
        },
        catchError(error) {
            const response = error.response

            this.error = {
                status: response.status,
                statusText: response.statusText,
                message: response.data?.message,
            }
        },
        slotsLeft(shift) {
            if (! this.isEmployee) {
                return shift.details.slots.taken
            }

            return shift.details.slots.left
        },
        slotsTotal(shift) {
            return shift.details.slots.total
        },
        async downloadSchedule() {
            this.exporting = true

            try {
                const { headers, data } = await axios.post('/api/reports/export', {
                    template: 'schedule',
                    type: 'excel',
                    today: this.today,
                    filters: this.filterValues,
                }, {
                    responseType: 'blob',
                })

                if (data) download(data, headers.filename)
            } catch (e) {
                window.toastr.error(e.response.data.message ?? this.$trans.get('__JSON__.Something went wrong'), '', {
                    positionClass: 'toast-bottom-right',
                })
            }

            this.exporting = false
        },
    },
    mounted() {
        this.fetch()
    },
}
</script>

<style>
.shift-card:hover{
    transform: scale(1.05);
    box-shadow: 0 10px 20px rgba(0,0,0,.12), 0 4px 8px rgba(0,0,0,.06);
    cursor: pointer;
}

.table-shifts tbody tr td, .table-shifts thead tr th {
    border-right: 1px solid #f3f3f3;
}

.table-shifts tbody tr td:last-child, .table-shifts thead tr th:last-child {
    border-right: none;
}

table.table-shifts thead th.today {
    position: relative;
    border-top: 3px solid #5844ea !important;
}

table.table-shifts thead th.today:after,table.table-shifts thead th.today :before {
    top: -4px;
    left: 50%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    transform: rotate(-180deg) translateY(-4px)
}

table.table-shifts thead th.today:after {
    border-color: none;
    border-bottom-color: #5844ea !important;
    border-width: 5px;
    margin-left: -5px
}

/* vue multiselect styling */
.multiselect {
    min-height: 22px;
}

.multiselect__select {
    display: none !important;
}

.multiselect__tags {
    min-height: 0;
    max-height: 14px !important;
    padding: 0;
    border: none;
}

/* flatpickr styling */
.flatpickr-day.today {
    border-color: #5844ea;
}

.flatpickr-day.selected, .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.selected.inRange, .flatpickr-day.startRange.inRange, .flatpickr-day.endRange.inRange, .flatpickr-day.selected:focus, .flatpickr-day.startRange:focus, .flatpickr-day.endRange:focus, .flatpickr-day.selected:hover, .flatpickr-day.startRange:hover, .flatpickr-day.endRange:hover, .flatpickr-day.selected.prevMonthDay, .flatpickr-day.startRange.prevMonthDay, .flatpickr-day.endRange.prevMonthDay, .flatpickr-day.selected.nextMonthDay, .flatpickr-day.startRange.nextMonthDay, .flatpickr-day.endRange.nextMonthDay {
    background: #5844ea;
    border-color: #5844ea;
}

.flatpickr-day.week.selected {
    border-radius: 0;
    -webkit-box-shadow: -5px 0 0 #5844ea, 5px 0 0 #5844ea;
    box-shadow: -5px 0 0 #5844ea, 5px 0 0 #5844ea;
}
</style>
